// Resolver helpers for compile-time `var(...)` expression tokens.
// Example: `var(--sizes[ind].name)` is resolved here.
import type { LuisArrayVariables, LuisObjectVariables } from '../../types'
import { isObjectVariableValue, parseObjectVariable } from '../../variables/object-variable'
import { getObjectVariableValue } from '../../variables/object-variable/keys'
import { resolveObjectKeyValue } from '../../variables/class-variable/transform/keys'
import { parseScopeValue } from './placeholder-expression-literals'
import { resolveBracketAccessKey, type ParsedExpressionVarCall } from './placeholder-expression-var-parse'
import type { LoopExpressionValue } from './placeholder-expression-types'

// Resolve one parsed `var(...)` expression token into a typed value.
export function resolveParsedVarExpressionValue(
  parsed: ParsedExpressionVarCall,
  scope: Record<string, string>,
  arrays: LuisArrayVariables,
  variables: Record<string, string>,
  objectVariables: LuisObjectVariables,
): LoopExpressionValue | null {
  if (parsed.accessKey === null && parsed.objectKey === null) {
    const value = variables[parsed.variableName]
    return typeof value === 'string' ? parseScopeValue(value) : null
  } else if (parsed.accessKey === null) {
    const value = getObjectVariableValue(objectVariables, parsed.variableName, parsed.objectKey!)
    return typeof value === 'string' ? parseScopeValue(value) : null
  } else {
    // Continue bracket-access var resolution.
  }

  const resolvedAccessKey = resolveBracketAccessKey(parsed.accessKey, scope)
  // Reject unresolved dynamic bracket selectors.
  if (resolvedAccessKey === null) return null
  const arrayValue = arrays[parsed.variableName]
  if (arrayValue) return resolveArrayAccessValue(arrayValue, resolvedAccessKey, parsed.objectKey, objectVariables)
  if (parsed.objectKey !== null) return null
  const value = getObjectVariableValue(objectVariables, parsed.variableName, resolvedAccessKey)
  return typeof value === 'string' ? parseScopeValue(value) : null
}

// Resolve array access and optional array-item object-key lookup.
function resolveArrayAccessValue(
  arrayValue: string[],
  accessKey: string,
  objectKey: string | null,
  objectVariables: LuisObjectVariables,
): LoopExpressionValue | null {
  // Reject non-numeric array selectors.
  if (!/^\d+$/.test(accessKey)) return null
  const item = arrayValue[Number.parseInt(accessKey, 10)]
  // Reject out-of-range array indexes.
  if (typeof item !== 'string') return null
  if (objectKey === null) return parseScopeValue(item)
  // Continue array-item object-key lookup.
  if (!isObjectVariableValue(item)) return null
  const parsed = parseObjectVariable(item, objectVariables)
  const resolved = resolveObjectKeyValue(parsed, objectKey)
  return typeof resolved === 'string' ? parseScopeValue(resolved) : null
}
